import { imageStateOf, readyRenditions } from '@/lib/mealImages'
import {
  type CookState,
  getCookTimeMinutes,
  type MealPlanResult,
  type MealPlanResultSlot,
  PlanCompletionReason,
  PlanScheduling,
  type StoredMeal
} from '@/types/mealPlan'
import type { DinnerView, PlanView } from './viewModels'

// Pure Firestore-shape → view-model mappers. This module is the ONLY place that
// reads the persistence shapes (MealPlanResult / MealPlanResultSlot / StoredMeal);
// keep it free of React and i18n so it stays trivially unit-testable and so a
// storage change lands here alone.

export function toPlanView(result: MealPlanResult): PlanView {
  return {
    id: result.id,
    name: result.name?.trim() || null,
    status: result.lifecycle,
    startDate: new Date((result.startDate ?? result.createdAt).toMillis()),
    scheduling: result.scheduling ?? PlanScheduling.Scheduled,
    completedAt: result.completedAt ? new Date(result.completedAt.toMillis()) : null,
    endedEarly: result.completionReason === PlanCompletionReason.EndedEarly,
    levelUpsEnabled: result.levelUpsEnabled,
    rating: result.rating ?? 0,
    notes: result.notes ?? ''
  }
}

// `meal` is undefined while its recipe is still loading, or for a failed slot.
// `cookState` is the plan's per-cook entry (absent = ready/untouched).
export function toDinnerView(
  slot: MealPlanResultSlot,
  slotIndex: number,
  meal: StoredMeal | undefined,
  cookState: CookState | undefined
): DinnerView {
  const cookId = slot.cookId ?? null
  const status: DinnerView['status'] = slot.mealId === null ? 'failed' : meal ? 'ready' : 'loading'
  return {
    id: cookId ?? slot.mealId ?? '',
    mealId: slot.mealId,
    slotIndex,
    dayOffset: slot.dayOffset ?? undefined,
    name: meal?.mealName ?? '',
    description: meal?.mealDescription ?? '',
    renditions: meal ? readyRenditions(meal) : undefined,
    imageState: meal ? imageStateOf(meal) : 'placeholder',
    cookMins: meal ? getCookTimeMinutes(meal) : 0,
    servings: meal?.servings,
    levelUp: (meal?.levelUps.length ?? 0) > 0,
    status,
    cookState: {
      cooked: cookState?.lifecycle === 'cooked',
      skipped: cookState?.lifecycle === 'skipped',
      note: cookState?.note ?? '',
      cookedAt: cookState?.cookedAt ? new Date(cookState.cookedAt.toMillis()) : undefined,
      cookingFinishedAt: cookState?.cookingFinishedAt ? new Date(cookState.cookingFinishedAt.toMillis()) : undefined
    },
    recipe: meal
  }
}
